import React from 'react';
import { useLocation } from 'react-router-dom';
import { useCookie } from '@/contexts/CookieContext';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

export const CookieBanner: React.FC = () => {
    const location = useLocation();
    const { showBanner, setConsentStatus } = useCookie();

    // Allow access to legal pages without consent banner
    const legalPages = ['/mentions-legales', '/politique-confidentialite'];
    const isOnLegalPage = legalPages.some(page => location.pathname.includes(page));

    if (isOnLegalPage || !showBanner) return null;

    const handleAccept = () => {
        setConsentStatus('accepted');
    };

    const handleRefuse = () => {
        setConsentStatus('refused');
    };

    return (
        <aside
            data-nosnippet="true"
            aria-label="Gestion des cookies"
            className="fixed bottom-3 inset-x-3 sm:bottom-5 sm:right-5 sm:inset-x-auto sm:max-w-lg z-50 animate-in fade-in slide-in-from-bottom-4 duration-300 pointer-events-auto"
        >
            <div className="bg-white/95 dark:bg-[hsl(228,14%,8%)]/95 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl p-4 sm:p-5 text-gray-900 dark:text-gray-100 ring-1 ring-black/5 dark:ring-white/5">
                <div className="flex items-start justify-between gap-3 mb-2.5">
                    <div className="flex items-center gap-2">
                        <span className="text-xl" role="img" aria-label="cookie">🍪</span>
                        <h2 className="text-sm font-semibold tracking-tight text-gray-900 dark:text-white">
                            Respect de votre vie privée
                        </h2>
                    </div>
                    <button
                        onClick={handleRefuse}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 rounded-md"
                        aria-label="Fermer et refuser"
                        title="Continuer sans accepter"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
                    Nous utilisons des traceurs internes pour <strong className="font-semibold text-gray-800 dark:text-gray-100">personnaliser votre expérience</strong> et vous proposer les meilleures <strong className="font-semibold text-gray-800 dark:text-gray-100">recommandations produits</strong>.
                    {' '}<span className="text-gray-500 dark:text-gray-400">Aucune donnée n'est vendue à des tiers et nos analyses restent strictement anonymes.</span>
                </p>

                <div className="flex flex-col-reverse sm:flex-row items-center gap-2 pt-1">
                    <Button
                        variant="outline"
                        onClick={handleRefuse}
                        className="w-full sm:w-1/2 text-xs font-medium border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 h-9"
                    >
                        Tout refuser
                    </Button>
                    <Button
                        onClick={handleAccept}
                        className="w-full sm:w-1/2 text-xs font-semibold bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white shadow-sm hover:shadow transition-all h-9"
                    >
                        Tout accepter
                    </Button>
                </div>

                <div className="mt-2.5 text-center sm:text-left">
                    <a
                        href="#/politique-confidentialite"
                        className="text-[11px] text-gray-500 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400 underline transition-colors"
                    >
                        En savoir plus dans notre politique de confidentialité
                    </a>
                </div>
            </div>
        </aside>
    );
};

